// Daily budget allocation math. Money runs on integer cents everywhere: `10000 / 3` in floats never
// sums back to `10000`, and the allocation is required to match the total exactly.
// Kept free of imports so the node:test suites can load it straight from source.

const DAY_MS = 24 * 60 * 60 * 1000;

const toCents = (amount) => Math.round((Number(amount) || 0) * 100);

export const roundToCents = (amount) => toCents(amount) / 100;

// One day's share of an even split: the remainder cents go to the earliest days so the parts always
// sum back to the total - otherwise "Split evenly" could never satisfy the match-the-total rule.
const getEvenShare = ({ budget, days, index }) => {
  const totalCents = toCents(budget);
  const baseCents = Math.floor(totalCents / days);
  const remainderCents = totalCents - baseCents * days;

  return (baseCents + (index < remainderCents ? 1 : 0)) / 100;
};

export const splitBudgetEvenly = (budget, days) => {
  if (!days || days < 1) {
    return [];
  }

  return Array.from({ length: days }, (_, index) => getEvenShare({ budget, days, index }));
};

export const sumDailyBudgets = (amounts) => (amounts || []).reduce((total, amount) => total + toCents(amount), 0) / 100;

export const isDailyBudgetBalanced = (budget, amounts) => toCents(sumDailyBudgets(amounts)) === toCents(budget);

// Signed gap between the total and what is allocated: positive means unallocated, negative means over.
export const getDailyBudgetDifference = (budget, amounts) =>
  (toCents(budget) - toCents(sumDailyBudgets(amounts))) / 100;

export const hasBudgetValue = (budget) => budget !== '' && budget !== null && budget !== undefined;

// How many days an allocation spans: a single-day event (or an unticked Multi-day) has nothing to split.
export const getDailyBudgetDays = ({ dateRange, isMultiDay }) => (isMultiDay ? getEventDays(dateRange).length : 1);

// The form value is never trimmed to the day count - a day that leaves the range and comes back
// (3 days -> 2 -> 4) keeps its amount. This reads it at the current length, and as no allocation at
// all without a budget or a second day, so validation and save see only what the admin sees.
export const getAppliedDailyBudgets = ({ budget, dailyBudgets, days }) =>
  hasBudgetValue(budget) && days > 1 && dailyBudgets?.length
    ? Array.from({ length: days }, (_, index) => dailyBudgets[index] ?? 0)
    : [];

// The key must not be `dailyBudgets`: final-form would then try to write the per-day errors into
// this value while resolving the registered `dailyBudgets[i]` fields, and throw on the numeric index.
export const validateDailyBudgetAllocation = ({ values: { budget, dailyBudgets }, days, isEnabled }) => {
  const amounts = getAppliedDailyBudgets({ budget, dailyBudgets, days });

  return isEnabled && amounts.length && !isDailyBudgetBalanced(budget, amounts)
    ? { dailyBudgetsTotal: true }
    : undefined;
};

export const getDailyBudgetAllocationError = ({ budget, amounts, t, formatMoney }) => {
  if (!amounts.length || isDailyBudgetBalanced(budget, amounts)) {
    return undefined;
  }

  const difference = getDailyBudgetDifference(budget, amounts);

  return difference > 0
    ? t('dailyBudgetRemainsUnallocated', { amount: formatMoney(difference) })
    : t('dailyBudgetExceedsBudget', { amount: formatMoney(Math.abs(difference)) });
};

// Cost as a rounded percentage of the budget; undefined (no %) when there is no cost or nothing to divide by.
export const getBudgetUtilization = (cost, budget) =>
  !cost || !budget ? undefined : Math.round((cost / budget) * 100);

const padDatePart = (value) => String(value).padStart(2, '0');

// Normalises an API date string, a moment or a Date to its 'YYYY-MM-DD' calendar day.
const toDateKey = (value) => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : undefined;
  }

  if (typeof value.format === 'function') {
    return value.isValid?.() === false ? undefined : value.format('YYYY-MM-DD');
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${padDatePart(value.getMonth() + 1)}-${padDatePart(value.getDate())}`;
  }

  return undefined;
};

// Calendar days are counted in UTC so a DST change never makes a day 23 or 25 hours long.
const toDayNumber = (dateKey) =>
  Date.UTC(Number(dateKey.slice(0, 4)), Number(dateKey.slice(5, 7)) - 1, Number(dateKey.slice(8, 10))) / DAY_MS;

const fromDayNumber = (dayNumber) => new Date(dayNumber * DAY_MS).toISOString().slice(0, 10);

const getDayNumberRange = ({ startDate, endDate }) => {
  const start = toDateKey(startDate);
  const end = toDateKey(endDate || startDate);

  if (!start || !end) {
    return undefined;
  }

  return { first: toDayNumber(start), last: toDayNumber(end) };
};

// The calendar days an event spans, as API-formatted date strings. Daily budgets are stored per day
// on the backend but held positionally in the form, and this is what maps between the two.
export const getEventDays = (dateRange) => {
  const range = dateRange && getDayNumberRange(dateRange);

  if (!range || range.last < range.first) {
    return [];
  }

  return Array.from({ length: range.last - range.first + 1 }, (_, index) => fromDayNumber(range.first + index));
};

// The form holds the allocation by day position, so a start date that moves would slide every amount onto
// another day. This shifts the positions by the days the start moved, keeping each amount on its own date:
// an earlier start puts 0 in front for the days that joined, a later one drops the days that left.
export const shiftDailyBudgetsToStartDate = ({ amounts, fromStartDate, toStartDate }) => {
  const from = toDateKey(fromStartDate);
  const to = toDateKey(toStartDate);

  if (!amounts?.length || !from || !to) {
    return amounts;
  }

  const offset = toDayNumber(from) - toDayNumber(to);

  if (offset > 0) {
    return [...Array(offset).fill(0), ...amounts];
  }

  return offset < 0 ? amounts.slice(-offset) : amounts;
};

// A project's budget for one of its days: the saved allocation when there is one, otherwise the even
// split the backend derives at read time.
export const getProjectDailyBudget = ({ budget, dailyBudgets, date, endDate }, day) => {
  if (dailyBudgets?.length) {
    return Number(dailyBudgets.find((dailyBudget) => dailyBudget.date === day)?.amount) || 0;
  }

  const range = getDayNumberRange({ startDate: date, endDate });
  const dayKey = toDateKey(day);

  if (!range || !dayKey) {
    return 0;
  }

  const days = range.last - range.first + 1;
  const index = toDayNumber(dayKey) - range.first;

  return index < 0 || index >= days ? 0 : getEvenShare({ budget, days, index });
};

export const getDayBudget = ({ projects, date }) =>
  projects.reduce((total, project) => total + getProjectDailyBudget(project, date), 0);

// Estimated labor cost of the events that have a budget on the day. An event without one has nothing to be
// utilized against, so its cost stays out of the % (it still shows in the labor cost row).
export const getBudgetedLaborCost = ({ projects, date, mappedProjectDays }) =>
  projects.reduce(
    (total, project) =>
      getProjectDailyBudget(project, date) > 0
        ? total + (mappedProjectDays?.[project.id]?.[date]?.expenses || 0)
        : total,
    0
  );

// A shift shows on the board when it has internal bookings or open slots. `stats.booked` already counts the
// supplier's part (max(externalBooked, externalOrdered), or only externalBooked when isProjectStatsActualBooked),
// so a shift fully ordered from a supplier has neither and the board shows nothing for it.
export const isShiftOnBudgetBoard = ({ shift, isProjectStatsActualBooked }) => {
  const { booked = 0, bookedStandBy = 0, externalBooked = 0, externalOrdered = 0 } = shift.stats || {};
  const externalCovered = isProjectStatsActualBooked ? externalBooked : Math.max(externalBooked, externalOrdered);
  const pendingOrdered = isProjectStatsActualBooked ? Math.max(0, externalOrdered - externalBooked) : 0;

  const internalBooked = booked + bookedStandBy - externalCovered;
  const open = (shift.needed || 0) - booked - bookedStandBy - pendingOrdered;

  return internalBooked > 0 || open > 0;
};

// The update that sets one day of an event to `amount`. A single-day event just takes it as its budget;
// a multi-day one keeps its other days as they are and grows or shrinks its total by the difference.
export const getDailyBudgetUpdate = ({ project, day, amount }) => {
  const days = getEventDays({ startDate: project.date, endDate: project.endDate });

  if (days.length <= 1) {
    return { budget: roundToCents(amount) };
  }

  const savedAmountByDate = project.dailyBudgets?.length
    ? Object.fromEntries(project.dailyBudgets.map((dailyBudget) => [dailyBudget.date, dailyBudget.amount]))
    : undefined;
  const evenSplit = savedAmountByDate ? undefined : splitBudgetEvenly(project.budget, days.length);

  const dailyBudgets = days.map((date, index) => {
    if (date === day) {
      return { date, amount: roundToCents(amount) };
    }

    return { date, amount: savedAmountByDate ? roundToCents(savedAmountByDate[date]) : evenSplit[index] };
  });

  return { budget: sumDailyBudgets(dailyBudgets.map((dailyBudget) => dailyBudget.amount)), dailyBudgets };
};

// Daily budgets travel as [{ date, amount }] but are edited positionally by day, so forms map between
// the two shapes on the way in and out.
export const toFormDailyBudgets = (dailyBudgets, dateRange) => {
  if (!dailyBudgets?.length) {
    return [];
  }

  const amountByDate = Object.fromEntries(dailyBudgets.map(({ date, amount }) => [date, amount]));

  return getEventDays(dateRange).map((date) => amountByDate[date] ?? 0);
};

export const toApiDailyBudgets = (amounts, dateRange) => {
  if (!amounts?.length) {
    return [];
  }

  return getEventDays(dateRange).map((date, index) => ({ date, amount: roundToCents(amounts[index]) }));
};
